import { useState, useEffect, useRef } from 'react';

export const INIT_REFRESH_TIMEOUT_MS = 3000;
/** How long to wait for the SDK to report a session before falling through. */
export const SDK_FLIP_TIMEOUT_MS = 2000;

export interface AuthInitDeps {
  isLoggedIn: boolean;
  isFetchingUserInfo: boolean;
  refreshToken: () => Promise<Response | undefined>;
}

/**
 * Decides when the app may render: either the SDK reports a session against a
 * valid cookie, or a refresh attempt has resolved one way or the other.
 *
 * Dependencies are injected rather than read from the SDK here, so this is
 * testable without mounting the router, the map or the data grid.
 */
export function useAuthInit({ isLoggedIn, isFetchingUserInfo, refreshToken }: AuthInitDeps): boolean {
  const [cookieValid, setCookieValid] = useState(false);
  const [refreshDone, setRefreshDone] = useState(false);

  // The init effect runs once and must not re-run, so it cannot see later
  // values of `isLoggedIn` through its closure. A ref gives the fallback timer
  // the CURRENT value when it fires, which is the only moment the question
  // "did the SDK ever flip?" can honestly be answered.
  const isLoggedInRef = useRef(isLoggedIn);
  useEffect(() => {
    isLoggedInRef.current = isLoggedIn;
  }, [isLoggedIn]);

  useEffect(() => {
    const atExpRaw = document.cookie
      .split('; ')
      .find((c) => c.startsWith('app.at_exp='))?.split('=')[1];
    const atExpMs = atExpRaw ? parseInt(atExpRaw, 10) * 1000 : 0;
    const atExpValid = atExpMs > Date.now();
    console.log('[Auth init]', {
      atExpValid,
      atExpInSeconds: atExpMs ? Math.round((atExpMs - Date.now()) / 1000) : null,
      isLoggedInAtMount: isLoggedIn,
    });

    if (atExpValid) {
      setCookieValid(true);
      window.sessionStorage.removeItem('em3:reloadedAfterInitRefresh');
      const sdkFallback = window.setTimeout(() => {
        // Only a diagnostic, and only true if the SDK really has not flipped.
        // Previously this fired unconditionally, so a stalled SDK and an
        // ordinary login produced identical output and the warning could never
        // report the condition it exists for.
        if (!isLoggedInRef.current) {
          console.warn('[Auth init] SDK isLoggedIn never flipped — falling through');
        }
        // The fall-through itself is unconditional: it exists to stop a stalled
        // SDK blocking render, and that must not depend on the diagnostic.
        setRefreshDone(true);
      }, SDK_FLIP_TIMEOUT_MS);
      return () => window.clearTimeout(sdkFallback);
    }

    let cancelled = false;
    let willReload = false;
    const timer = window.setTimeout(() => {
      if (cancelled || willReload) return;
      console.warn('[Auth init] refresh timed out — falling through to SDK state');
      setRefreshDone(true);
    }, INIT_REFRESH_TIMEOUT_MS);

    refreshToken()
      .then((res) => {
        if (cancelled) return;
        const ok = res?.ok ?? false;
        console.log('[Auth init] refresh result', { ok, status: res?.status });
        if (ok && !window.sessionStorage.getItem('em3:reloadedAfterInitRefresh')) {
          willReload = true;
          window.sessionStorage.setItem('em3:reloadedAfterInitRefresh', '1');
          console.log('[Auth init] reloading to let SDK pick up fresh cookies');
          window.location.reload();
          return;
        }
        window.sessionStorage.removeItem('em3:reloadedAfterInitRefresh');
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn('[Auth init] refresh threw', (err as Error)?.message ?? err);
      })
      .finally(() => {
        if (cancelled || willReload) return;
        window.clearTimeout(timer);
        setRefreshDone(true);
      });

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const initialized = (cookieValid && isLoggedIn) || refreshDone;

  useEffect(() => {
    console.log('[Auth init] state', { cookieValid, refreshDone, isLoggedIn, isFetchingUserInfo, initialized });
  }, [cookieValid, refreshDone, isLoggedIn, isFetchingUserInfo, initialized]);

  return initialized;
}
